import React from 'react'

import { BaseForm } from 'components/Form'
import Loader from 'components/Loader'
import {
  FormButtons,
  ErrorScreen,
  convertToISOString,
  getCategoryDetails,
  getPartnerDetails,
  getFilteredList,
  validatePartnerOnlineCodes,
  validateUseByDate,
  getFormattedPartnerOnlineCodes,
  getFormattedDate,
  getFormattedTime,
  getDateValues,
} from 'components/Rewards/utils'
import {
  formatPromotionalPrice,
  getFormattedPromotionalPrice,
  formatUsuageValidityData,
  formatMetaData,
  fetchSegments,
} from 'components/Rewards/Catalogue/CatalogueUtils'
import { handleApis, handleError } from 'components/Rewards/services'
import API from 'lib/api'
import TextInfo from 'components/Rewards/Catalogue/TextInfo'
import CategoryInfo from 'components/Rewards/CategoryInfo'
import ImageUrl from 'components/Rewards/Catalogue/ImageUrl'
import Details from 'components/Rewards/Catalogue/Details'
import RedemptionValidity from 'components/Rewards/Catalogue/RedemptionValidity'
import UsageValidity from 'components/Rewards/Catalogue/UsageValidity'
import LinkpointPrice from 'components/Rewards/Catalogue/LinkpointPrice'
import RedemptionLimit from 'components/Rewards/Catalogue/RedemptionLimit'
import DisplayCreatorEditorInfo from 'components/Rewards/DisplayCreatorEditorInfo'
import {
  getOfferDetails,
  getCouponTemplateDetails,
} from 'components/Rewards/Catalogue/OfferField'
import { getMessage } from 'lib/translator'

class CatalogueForm extends BaseForm {
  constructor(props) {
    super(props)
    this.state = {
      values: {
        linkpointPrice: {
          regular: '',
          priceChanges: [],
        },
        status: 1,
        usageType: 'Fixed',
        redemptionCap: {
          isCapTotal: false,
          isCapPerUser: false,
        },
        memberships: [],
        metadata: {
          product: {
            offerId: '',
            dspTemplateCode: '',
            description: '',
            offerError: '',
          },
        },
      },
    }
  }

  async componentDidMount() {
    const values = Object.assign({}, this.state.values)
    values.loading = true
    this.setState({
      values,
    })
    let getRewardsDetails = ''
    const { id } = { ...this.props.value }
    if (id) {
      const api = new API({ url: `/rms/v2/rewards/${id}` })
      getRewardsDetails = api.get()
    }

    try {
      const catDetailsApi = new API({
        url: `/rms/categories?status=active&offset=0&limit=1000`,
      })
      const partnerDetailsApi = new API({
        url: `/rms/partners?offset=0&limit=1000`,
      })
      const [catDetailsRes, partnerDetailsRes, rewardsDetailsRes] =
        await Promise.allSettled([
          catDetailsApi.get(),
          partnerDetailsApi.get(),
          getRewardsDetails,
        ])
      getCategoryDetails(catDetailsRes, values)
      getPartnerDetails(partnerDetailsRes, values)

      let formattedLinkPointPrice = ''
      let offerDetails = ''
      let segmentDetails = ''

      if (rewardsDetailsRes.status === 'fulfilled' && rewardsDetailsRes.value) {
        const rewardsResValue = rewardsDetailsRes.value
        const startDateTime = rewardsResValue.startAt
        const endDateTime = rewardsResValue.endAt
        const usedByDateTime = rewardsResValue.useByDate
        const dateValues = getDateValues(startDateTime, endDateTime)
        values.date = { ...dateValues }
        values.usedByDate = getFormattedDate(usedByDateTime)
        values.usedByTime = getFormattedTime(usedByDateTime)
        values.usageType = rewardsResValue.useWithinDays ? 'Relative' : 'Fixed'
        const {
          linkpointPrice: { regular = '', priceChanges = [] },
        } = rewardsResValue
        formattedLinkPointPrice = {
          linkpointPrice: {
            regular,
            priceChanges: getFormattedPromotionalPrice(priceChanges),
          },
        }

        const product = rewardsResValue?.metadata?.product ?? {}
        const offerId = product.offerId ?? ''
        const dspTemplateCode = product.dspTemplateCode ?? ''
        let productDetails = product

        if (offerId) {
          const { description, offerError } = await getOfferDetails(offerId)
          productDetails = {
            ...productDetails,
            offerId,
            description,
            offerError,
          }
        }

        if (dspTemplateCode) {
          const { couponTitle, couponError } =
            await getCouponTemplateDetails(dspTemplateCode)
          productDetails = {
            ...productDetails,
            couponTitle,
            couponError,
          }
        }

        if (offerId || dspTemplateCode) {
          offerDetails = {
            metadata: {
              ...rewardsResValue.metadata,
              product: productDetails,
            },
          }
        }

        const segmentIds = rewardsResValue?.metadata?.segmentIds ?? []

        if (segmentIds.length > 0) {
          const segmentIdsDetails = await fetchSegments(
            rewardsResValue?.metadata?.segmentIds
          )
          segmentDetails = {
            metadata: {
              ...rewardsResValue.metadata,
              // spread after offerDetails, so carry the product across
              product: productDetails,
              segmentIds: segmentIdsDetails,
            },
          }
        }
      }
      if (rewardsDetailsRes.status === 'rejected') {
        throw new Error(rewardsDetailsRes.reason.message)
      }

      values.loading = false
      this.setState({
        values: {
          ...values,
          ...rewardsDetailsRes.value,
          ...formattedLinkPointPrice,
          ...rewardsDetailsRes.value.redemptionCap,
          ...rewardsDetailsRes.value.imgUrls,
          ...offerDetails,
          ...segmentDetails,
        },
      })
    } catch (err) {
      handleError(err, this)
    }
  }

  async _submitHandler(e) {
    e.preventDefault()
    const { values } = this.state
    this.beforeSubmit(values)

    this.setState({ pressedSubmitWithCurrentData: true })

    const {
      name,
      slug,
      type,
      status,
      banner,
      date,
      usageType,
      tnc,
      description,
      categoryIds,
      partnerId,
      storeIds,
      linkpointPrice,
      howToRedeem,
      isCapTotal,
      isCapPerUser,
      capTotal,
      capPerUser,
      title,
      subtitle,
      metadata,
      partnerOnlineCodes,
      memberships,
    } = values

    const formattedPromotionalPrice = formatPromotionalPrice(
      linkpointPrice?.priceChanges
    )
    const startAt = convertToISOString(date?.startingDate, date?.startingTime)
    const endAt = convertToISOString(date?.endingDate, date?.endingTime)

    let isValid = true
    let isValidTypePartnerOnline = true
    let isValidUseByDate = true
    let isValidOfferID = true

    const isTypePartnerOnline =
      this.props.method === 'add' &&
      type === 'PARTNER_ONLINE' &&
      partnerOnlineCodes
    let formattedPartnerOnlineCodes = ''

    const isTypeFPGProduct = type === 'FPG_PRODUCT_VOUCHER'

    if (isTypePartnerOnline) {
      const filteredList = getFilteredList({ partnerOnlineCodes })
      isValidTypePartnerOnline = validatePartnerOnlineCodes(
        filteredList,
        values
      )
      formattedPartnerOnlineCodes = getFormattedPartnerOnlineCodes(filteredList)
    }

    let productOfferError = ''
    if (isTypeFPGProduct) {
      const offerId = metadata?.product?.offerId ?? ''
      const dspTemplateCode = (metadata?.product?.dspTemplateCode ?? '').trim()

      if (!offerId && !dspTemplateCode) {
        productOfferError = getMessage('rewards.catalogue.product.atLeastOne')
        isValidOfferID = false
      } else if (offerId) {
        const { offerError } = await getOfferDetails(offerId)
        productOfferError = offerError
        isValidOfferID = !offerError
      }
      values.metadata = {
        ...values.metadata,
        product: {
          ...values.metadata?.product,
          offerError: productOfferError,
        },
      }
    }

    if (usageType === 'Fixed') {
      isValidUseByDate = validateUseByDate(values)
    }

    this.setState({ values: { ...values } })

    // Strip validations for fields that aren't rendered for the current type
    // so stale entries left behind by unmounted inputs don't block submission.
    const scopedValidations = { ...(this.state.validations || {}) }
    if (scopedValidations.metadata) {
      const scopedMetadataValidations = { ...scopedValidations.metadata }
      if (type !== 'FP_EVOUCHER') {
        delete scopedMetadataValidations.voucher
      }
      if (type !== 'FPG_PRODUCT_VOUCHER') {
        delete scopedMetadataValidations.product
      }
      scopedValidations.metadata = scopedMetadataValidations
    }

    isValid =
      isValidTypePartnerOnline &&
      isValidUseByDate &&
      isValidOfferID &&
      this.isFormValid(scopedValidations)

    if (isValid) {
      const mapData = {
        name,
        slug,
        type,
        tnc,
        description,
        title,
        subtitle,
        categoryIds,
        partnerId: Number(partnerId),
        storeIds,
        startAt,
        endAt,
        status: Number(status),
        imgUrls: { banner },
        redemptionCap: {
          isCapTotal,
          capTotal: isCapTotal ? capTotal : 0,
          isCapPerUser,
          capPerUser: isCapPerUser ? capPerUser : 0,
        },
        howToRedeem,
        linkpointPrice: {
          regular: linkpointPrice.regular,
          priceChanges: formattedPromotionalPrice,
        },
        memberships,
        ...(isTypePartnerOnline && {
          partnerOnlineCodes: formattedPartnerOnlineCodes,
        }),
      }

      formatUsuageValidityData(mapData, values)
      formatMetaData(mapData, metadata, type)
      const { id } = { ...this.props.value }
      handleApis(mapData, `/rms/v2/rewards`, this, id)
    }
  }

  render() {
    const {
      categoryList,
      fetchCategoryError,
      partnerList,
      fetchPartnerError,
      error,
      loading,
      isCapTotal,
      isCapPerUser,
      usageType,
      isUseByDateNull,
      isInvalidPartnerOnlineReward,
    } = {
      ...this.state.values,
    }
    const { Form } = this.components

    return (
      <div className="rewards container catelogue-form">
        {loading ? (
          <Loader size="sm" />
        ) : (
          <Form>
            <Details
              _this={this}
              partnerList={partnerList}
              fetchPartnerError={fetchPartnerError}
              isInvalidPartnerOnlineReward={isInvalidPartnerOnlineReward}
            />
            {(categoryList || fetchCategoryError) && (
              <CategoryInfo
                _this={this}
                categoryList={categoryList}
                fetchCategoryError={fetchCategoryError}
                type="catalogue"
                isRewardsCatalogue
              />
            )}
            <br />
            <TextInfo _this={this} />
            <ImageUrl _this={this} />
            <RedemptionValidity _this={this} />
            <UsageValidity
              _this={this}
              usageType={usageType}
              isUseByDateNull={isUseByDateNull}
            />
            <LinkpointPrice _this={this} />
            <RedemptionLimit
              _this={this}
              isCapTotal={isCapTotal}
              isCapPerUser={isCapPerUser}
            />
            {this.props.method === 'edit' && (
              <DisplayCreatorEditorInfo {...this.state.values} />
            )}
            <FormButtons buttons={this.buttons} type="catalogue" />
            {error && <ErrorScreen error={error} />}
          </Form>
        )}
      </div>
    )
  }
}
export default CatalogueForm
